'use client'

import { noop } from 'lodash'
import { createContext, ReactNode, useContext, useMemo, useState } from 'react'

// Data module can only import data type modules - ignore module boundaries rule
// Keep this until new utils/types module is introduced that is importable by data module
// Temporary workaround. Resolve ASAP. Do not add more ignores like this.
// eslint-disable-next-line eslint-comments/no-restricted-disable
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useDebounce } from '@marketplace-web/shared/ui-helpers'
import { UiState } from '@marketplace-web/shared/ui-state-util'

import { BookDetailsModel } from '../types/book-details'
import { IsbnContextType } from '../types/context'
import { UiStateContext } from './UiStateContext'
import { IsbnValidity, REQUEST_DELAY, ItemUploadFieldName } from '../constants'
import { transformBookDetails } from '../transformers/book-details'
import { getBookDetails } from '../api'

export const IsbnContext = createContext<IsbnContextType>({
  isbnUiState: UiState.Idle,
  bookDetails: null,
  fetchBookDetails: noop,
  setBookDetails: noop,
  setIsbnUiState: noop,
})

export const IsbnContextProvider = ({ children }: { children: ReactNode }) => {
  const [isbnUiState, setIsbnUiState] = useState<UiState>(UiState.Idle)
  const {
    errors: [errors, setErrors],
  } = useContext(UiStateContext)
  const [bookDetails, setBookDetails] = useState<BookDetailsModel | null>(null)

  const fetchBookDetails = useDebounce(
    async (isbn: string | null, validity: IsbnValidity) => {
      if (!isbn) {
        setBookDetails(null)

        return
      }

      if (validity === IsbnValidity.Unvalidated || validity === IsbnValidity.Invalid) return

      setIsbnUiState(UiState.Pending)

      const response = await getBookDetails({ isbn })

      if ('errors' in response) {
        setErrors({
          byName: { ...errors.byName, [ItemUploadFieldName.Isbn]: response.message },
          names: [...errors.names, ItemUploadFieldName.Isbn],
        })
        setIsbnUiState(UiState.Failure)

        return
      }

      setBookDetails(transformBookDetails(response))
      setIsbnUiState(UiState.Success)
    },
    REQUEST_DELAY,
    false,
  )

  const providerValue = useMemo(
    () => ({
      bookDetails,
      fetchBookDetails,
      isbnUiState,
      setBookDetails,
      setIsbnUiState,
    }),
    [bookDetails, fetchBookDetails, isbnUiState, setBookDetails, setIsbnUiState],
  )

  return <IsbnContext.Provider value={providerValue}>{children}</IsbnContext.Provider>
}

export const useIsbnContext = () => {
  const context = useContext(IsbnContext)

  if (!context) {
    throw new Error('useIsbnContext must be used within a IsbnContextProvider')
  }

  return context
}
